import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRightIcon, CheckIcon } from '../components/icons';
import { cx, FOCUS_RING_SURFACE } from '../components/styles';
import { useTour } from './TourContext';
import { TourStep } from './tourSteps';

/**
 * Draws the current tour step: a spotlight cut out of a dim scrim around the
 * anchored element, and a card beside it with the copy and the controls.
 *
 * Portalled to `document.body` and stacked above everything in the shell
 * (admin banner 60 · drawer 65 · palette 70 · tour 80). Carries its own
 * `data-v2-root` so `newui.css` — including the reduced-motion block —
 * reaches it outside the shell's subtree.
 *
 * Click handling: four transparent panels around the cutout absorb clicks
 * everywhere except on the spotlighted element. On an interactive step the
 * hole is left open, so clicking the real tab navigates like a normal click
 * and the provider advances on the route change. On a passive step a fifth
 * panel covers the hole too.
 */

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

type Placement = TourStep['placement'];

const PAD = 6;
const GAP = 14;
const MARGIN = 12;
/** Below this width the card is a bottom sheet rather than a side tooltip. */
const SHEET_BREAKPOINT = 640;
const TARGET_POLL_MS = 120;
/** How long to wait for an anchor before asking for the drawer. */
const DRAWER_AFTER_MS = 400;
/** How long to wait for an anchor before giving up on a spotlight. */
const FALLBACK_AFTER_MS = 2500;
/** Optional steps give up sooner, and skip rather than centre. */
const OPTIONAL_SKIP_AFTER_MS = 1500;

function isVisible(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function findTarget(name: string): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`);
  for (let i = 0; i < nodes.length; i += 1) {
    if (isVisible(nodes[i])) return nodes[i];
  }
  return null;
}

function toRect(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

interface CardPos {
  top: number;
  left: number;
  placement: Placement;
}

/**
 * Put the card on the preferred side of the cutout, falling back through the
 * other sides when it would leave the viewport, and clamp the result.
 */
function placeCard(
  hole: Rect,
  card: { width: number; height: number },
  viewport: { width: number; height: number },
  preferred: Placement,
): CardPos {
  const fallbacks: Placement[] = ['right', 'bottom', 'left', 'top'];
  const order: Placement[] = [preferred, ...fallbacks.filter((p) => p !== preferred)];
  const fits = (pos: CardPos) =>
    pos.left >= MARGIN &&
    pos.top >= MARGIN &&
    pos.left + card.width <= viewport.width - MARGIN &&
    pos.top + card.height <= viewport.height - MARGIN;

  const candidate = (placement: Placement): CardPos => {
    switch (placement) {
      case 'right':
        return {
          placement,
          left: hole.left + hole.width + GAP,
          top: hole.top + hole.height / 2 - card.height / 2,
        };
      case 'left':
        return {
          placement,
          left: hole.left - GAP - card.width,
          top: hole.top + hole.height / 2 - card.height / 2,
        };
      case 'top':
        return {
          placement,
          left: hole.left + hole.width / 2 - card.width / 2,
          top: hole.top - GAP - card.height,
        };
      case 'bottom':
      default:
        return {
          placement: 'bottom',
          left: hole.left + hole.width / 2 - card.width / 2,
          top: hole.top + hole.height + GAP,
        };
    }
  };

  const clamp = (pos: CardPos): CardPos => ({
    placement: pos.placement,
    left: Math.min(Math.max(pos.left, MARGIN), Math.max(MARGIN, viewport.width - MARGIN - card.width)),
    top: Math.min(Math.max(pos.top, MARGIN), Math.max(MARGIN, viewport.height - MARGIN - card.height)),
  });

  for (const placement of order) {
    const pos = candidate(placement);
    if (fits(pos)) return pos;
  }
  // Side placements clamp vertically without covering the target; a bottom
  // placement that overflows is clamped last because it may overlap.
  for (const placement of order) {
    const pos = clamp(candidate(placement));
    const overlaps =
      pos.left < hole.left + hole.width &&
      pos.left + card.width > hole.left &&
      pos.top < hole.top + hole.height &&
      pos.top + card.height > hole.top;
    if (!overlaps) return pos;
  }
  return clamp(candidate('bottom'));
}

const BUTTON_BASE = 'rounded-lg px-3.5 py-2 text-sm font-medium transition-colors';
const BUTTON_PRIMARY = cx(
  BUTTON_BASE,
  'inline-flex items-center gap-1.5 bg-[#3b82f6] text-white hover:bg-[#2563eb]',
  FOCUS_RING_SURFACE,
);
const BUTTON_SECONDARY = cx(
  BUTTON_BASE,
  'border border-white/10 bg-white/5 text-white hover:bg-white/10',
  FOCUS_RING_SURFACE,
);
const BUTTON_GHOST = cx(
  BUTTON_BASE,
  'text-white/50 hover:bg-white/[0.05] hover:text-white/80',
  FOCUS_RING_SURFACE,
);

export const TourOverlay: React.FC = () => {
  const { active, step, index, steps, next, back, skip, finish, skipMissingAnchor, drawer } = useTour();
  const [rect, setRect] = useState<Rect | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const [cardPos, setCardPos] = useState<CardPos | null>(null);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
  }));
  const cardRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const targetRef = useRef<HTMLElement | null>(null);

  const stepId = step?.id ?? null;
  const targetName = step?.target ?? null;
  const optional = Boolean(step?.optional);
  const skipRef = useRef(skipMissingAnchor);
  skipRef.current = skipMissingAnchor;
  const centred = !targetName || gaveUp || !rect;
  const isSheet = viewport.width < SHEET_BREAKPOINT;

  // Track the viewport for placement and the sheet breakpoint.
  useEffect(() => {
    if (!active) return;
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [active]);

  // Find and follow the anchor for the current step.
  useEffect(() => {
    targetRef.current = null;
    setRect(null);
    setGaveUp(false);
    if (!active || !targetName) return;

    let cancelled = false;
    let observer: ResizeObserver | null = null;
    let askedForDrawer = false;
    const startedAt = Date.now();

    const measure = () => {
      const el = targetRef.current;
      if (!el || !isVisible(el)) return;
      const nextRect = toRect(el);
      setRect((prev) => (sameRect(prev, nextRect) ? prev : nextRect));
    };

    const attach = (el: HTMLElement) => {
      targetRef.current = el;
      // Sidebar items only need to be on screen; a page control is centred
      // so a tall panel (the plan cards, a form) is not left half clipped at
      // the bottom edge with its card squeezed into the remaining strip.
      el.scrollIntoView({ block: targetName.startsWith('nav-') ? 'nearest' : 'center', inline: 'nearest' });
      measure();
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(measure);
        observer.observe(el);
      }
    };

    const poll = window.setInterval(() => {
      if (cancelled) return;
      const current = targetRef.current;
      if (current && isVisible(current)) {
        measure();
        return;
      }
      const found = findTarget(targetName);
      if (found) {
        if (observer && current) observer.unobserve(current);
        attach(found);
        return;
      }
      const waited = Date.now() - startedAt;
      // An optional control that never appears is not on this account's
      // page: move on rather than explain something that is not there.
      if (optional && waited >= OPTIONAL_SKIP_AFTER_MS) {
        cancelled = true;
        window.clearInterval(poll);
        skipRef.current();
        return;
      }
      // The desktop rail is hidden below `lg`, so a nav anchor only exists
      // inside the drawer. Ask the shell to open it once, then keep polling.
      if (!askedForDrawer && waited >= DRAWER_AFTER_MS && targetName.startsWith('nav-') && drawer) {
        askedForDrawer = true;
        drawer.open();
      }
      if (waited >= FALLBACK_AFTER_MS) setGaveUp(true);
    }, TARGET_POLL_MS);

    const immediate = findTarget(targetName);
    if (immediate) attach(immediate);

    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      observer?.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [active, stepId, targetName, optional, drawer]);

  // Page steps read better with the drawer out of the way.
  useEffect(() => {
    if (!active || !step) return;
    if (step.kind === 'page' && drawer) drawer.close();
  }, [active, step, drawer]);

  // Place the card once its size is known, and again whenever the hole moves.
  useLayoutEffect(() => {
    if (!active || centred || !rect || isSheet) {
      setCardPos(null);
      return;
    }
    const el = cardRef.current;
    if (!el) return;
    const hole: Rect = {
      top: rect.top - PAD,
      left: rect.left - PAD,
      width: rect.width + PAD * 2,
      height: rect.height + PAD * 2,
    };
    const size = { width: el.offsetWidth, height: el.offsetHeight };
    const pos = placeCard(hole, size, viewport, step?.placement ?? 'bottom');
    setCardPos((prev) =>
      prev && prev.top === pos.top && prev.left === pos.left && prev.placement === pos.placement
        ? prev
        : pos,
    );
  }, [active, centred, rect, viewport, isSheet, step?.placement, stepId]);

  // Keyboard: arrows move, Enter advances. Escape is deliberately ignored so a
  // reflex press does not throw the tour away; Skip is explicit.
  useEffect(() => {
    if (!active || !step) return;
    const onKey = (e: KeyboardEvent) => {
      const inField = (e.target as HTMLElement | null)?.closest('input, textarea, select');
      if (inField) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (step.kind === 'done') finish(step.ctaRoute);
        else next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, step, next, back, finish]);

  // Move focus to the primary control on each step so keyboard and
  // screen-reader users land on the card.
  useEffect(() => {
    if (!active) return;
    const id = window.setTimeout(() => primaryRef.current?.focus({ preventScroll: true }), 30);
    return () => window.clearTimeout(id);
  }, [active, stepId]);

  const onPrimary = useCallback(() => {
    if (!step) return;
    if (step.kind === 'done') finish(step.ctaRoute);
    else next();
  }, [step, next, finish]);

  if (!active || !step) return null;

  const total = steps.length;
  const hole: Rect | null =
    !centred && rect
      ? {
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
        }
      : null;

  const progress = total > 0 ? ((index + 1) / total) * 100 : 0;
  const isFirst = index <= 0;
  const isLast = step.kind === 'done';

  const primaryLabel =
    step.kind === 'welcome'
      ? 'Start the tour'
      : step.kind === 'done'
        ? step.ctaLabel ?? 'Finish'
        : step.kind === 'nav' && step.interactive
          ? 'Open it for me'
          : 'Next';

  const card = (
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="v2-tour-title"
      aria-describedby="v2-tour-body"
      className={cx(
        'v2-tour-card pointer-events-auto flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#121212] p-5 text-left shadow-2xl shadow-black/60',
        centred
          ? 'w-[calc(100%-2rem)] max-w-md'
          : isSheet
            ? 'fixed inset-x-3 bottom-3'
            : 'absolute w-[340px] max-w-[calc(100vw-1.5rem)]',
      )}
      style={
        !centred && !isSheet && cardPos
          ? { top: cardPos.top, left: cardPos.left }
          : !centred && !isSheet
            ? { top: -9999, left: -9999 }
            : undefined
      }
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/35">
          {step.kind === 'welcome' ? 'Quick tour' : `Step ${index + 1} of ${total}`}
        </p>
        {step.kind === 'nav' && step.interactive && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#3b82f6]/15 px-2 py-0.5 text-[11px] font-semibold text-[#60a5fa]">
            <ArrowRightIcon className="h-3 w-3" />
            Click the highlighted tab
          </span>
        )}
      </div>

      <div className="h-1 w-full overflow-hidden rounded-full bg-white/[0.06]" aria-hidden="true">
        <div
          className="h-full rounded-full bg-[#3b82f6] transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div>
        <h2 id="v2-tour-title" className="text-base font-semibold tracking-tight text-white">
          {step.title}
        </h2>
        <p id="v2-tour-body" className="mt-1.5 text-sm leading-relaxed text-white/60">
          {step.body}
        </p>
        {step.note && (
          <p className="mt-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-300/90">
            {step.note}
          </p>
        )}
      </div>

      <div className="mt-1 flex items-center justify-between gap-2">
        {isLast ? (
          <button type="button" onClick={() => finish()} className={BUTTON_GHOST}>
            Close
          </button>
        ) : (
          <button type="button" onClick={skip} className={BUTTON_GHOST}>
            {step.kind === 'welcome' ? 'Maybe later' : 'Skip tour'}
          </button>
        )}
        <div className="flex items-center gap-2">
          {!isFirst && !isLast && (
            <button type="button" onClick={back} className={BUTTON_SECONDARY}>
              Back
            </button>
          )}
          <button ref={primaryRef} type="button" onClick={onPrimary} className={BUTTON_PRIMARY}>
            {primaryLabel}
            {isLast ? <CheckIcon className="h-4 w-4" /> : <ArrowRightIcon className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <div data-v2-root="tour" className="v2-tour-root pointer-events-none fixed inset-0 z-[80] font-sans">
      {hole ? (
        <>
          {/* The dim scrim is one element with a huge shadow so the hole animates. */}
          <div
            aria-hidden="true"
            className={cx(
              'v2-tour-hole absolute rounded-xl',
              step.interactive ? 'v2-tour-pulse' : 'ring-2 ring-[#3b82f6]/60',
            )}
            style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
          />
          {/* Click blockers around the hole. */}
          <div className="pointer-events-auto absolute inset-x-0 top-0" style={{ height: Math.max(0, hole.top) }} />
          <div
            className="pointer-events-auto absolute inset-x-0 bottom-0"
            style={{ top: hole.top + hole.height }}
          />
          <div
            className="pointer-events-auto absolute left-0"
            style={{ top: hole.top, height: hole.height, width: Math.max(0, hole.left) }}
          />
          <div
            className="pointer-events-auto absolute right-0"
            style={{ top: hole.top, height: hole.height, left: hole.left + hole.width }}
          />
          {!step.interactive && (
            <div
              className="pointer-events-auto absolute"
              style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
            />
          )}
          {card}
        </>
      ) : (
        <div className="pointer-events-auto absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          {card}
        </div>
      )}
    </div>,
    document.body,
  );
};
